import { useEffect, useState } from 'react'
import { Archive, Plus, Save, X } from 'lucide-react'
import type { Kleur, Label, Project, ProjectStatus, Repo } from '../../../gedeeld/types'
import { api, github, repoUit } from '../../lib/api'
import { KLEUREN, naarInvoer, PROJECT_STATUS, vanInvoer } from '../../lib/opmaak'
import { navigeer } from '../../lib/router'
import { laadProjecten, meld, meldFout, useBasis, useStaat } from '../../lib/staat'
import { Avatar, Kies, Knop, Veld, vraagBevestiging } from '../ui'

/* ===========================================================================
 *  Instellingen van een project
 *
 *  Met één opslaanknop, anders dan de taak: een sleutel die per toets
 *  verandert zou bij elke letter de nummers van alle taken anders laten
 *  heten, en dat zien mensen die op dat moment naar het bord kijken.
 * =========================================================================== */

export default function Instellingen({ project, leden, opOpgeslagen, opLeden }: {
  project: Project
  leden: { id: string; rol: string }[]
  opOpgeslagen: (p: Project) => void
  opLeden: (l: { id: string; rol: string }[]) => void
}) {
  const { mensen } = useBasis()
  const githubAan = useStaat((s) => s.github.ingesteld)
  const org = useStaat((s) => s.github.org)
  const [naam, setNaam] = useState(project.naam)
  const [sleutel, setSleutel] = useState(project.sleutel ?? '')
  const [omschrijving, setOmschrijving] = useState(project.omschrijving)
  const [kleur, setKleur] = useState<Kleur>(project.kleur)
  const [status, setStatus] = useState<ProjectStatus>(project.status)
  const [repo, setRepo] = useState(repoUit(project.githubRepo) ?? '')
  const [start, setStart] = useState(naarInvoer(project.startAt))
  const [eind, setEind] = useState(naarInvoer(project.eindAt))
  const [eigenaar, setEigenaar] = useState(project.eigenaar ?? '')
  const [labels, setLabels] = useState<Label[]>(project.labels)
  const [nieuwLabel, setNieuwLabel] = useState('')
  const [repos, setRepos] = useState<Repo[]>([])
  const [team, setTeam] = useState(leden)
  const [bezig, setBezig] = useState(false)

  useEffect(() => {
    if (githubAan) github.repos().then(setRepos).catch(() => setRepos([]))
  }, [githubAan])

  async function bewaar() {
    setBezig(true)
    try {
      const p = await api.projectOpslaan({
        id: project.id, naam, sleutel: sleutel || null, omschrijving, kleur, status,
        githubRepo: repo ? `${org}/${repo}` : null,
        startAt: vanInvoer(start), eindAt: vanInvoer(eind), eigenaar: eigenaar || null, labels,
      })
      opOpgeslagen(p)
      if (JSON.stringify(team) !== JSON.stringify(leden)) {
        await api.ledenZetten(project.id, team)
        opLeden(team)
      }
      meld('Opgeslagen')
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  async function archief() {
    const naar = !project.archief
    if (naar && !(await vraagBevestiging(`${project.naam} in het archief zetten? De taken blijven bestaan en blijven in Werk staan bij wie ze heeft.`, 'Archiveren'))) return
    try {
      opOpgeslagen(await api.projectArchiveren(project.id, naar))
      await laadProjecten()
      meld(naar ? 'In het archief gezet' : 'Uit het archief gehaald')
      if (naar) navigeer('/projecten')
    } catch (e) { meldFout(e) }
  }

  const kandidaten = mensen.filter((m) => m.rollen.includes('developer') || m.rollen.includes('management') || team.some((t) => t.id === m.id))

  return (
    <div className="instellingen">
      <section className="vlak">
        <div className="vlak-kop"><h2>Algemeen</h2></div>
        <div className="formulier">
          <Veld label="Naam"><input className="invoer" value={naam} onChange={(e) => setNaam(e.target.value)} /></Veld>
          <Veld label="Sleutel" hulp={sleutel !== (project.sleutel ?? '') ? `Alle taken heten na opslaan ${sleutel || '#'}-…; hun nummers blijven gelijk.` : 'Waar de taaknummers mee beginnen.'}>
            <input className="invoer mono" value={sleutel} maxLength={10}
              onChange={(e) => setSleutel(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
          </Veld>
          <Veld label="Status">
            <Kies<ProjectStatus> waarde={status} onKies={setStatus}
              opties={(Object.keys(PROJECT_STATUS) as ProjectStatus[]).map((s) => ({ key: s, label: PROJECT_STATUS[s] }))} />
          </Veld>
          <Veld label="Kleur">
            <div className="kleurkeuze">
              {KLEUREN.map((k) => <button key={k} type="button" className={`kleur-bol t-${k} ${kleur === k ? 'actief' : ''}`} onClick={() => setKleur(k)} aria-label={k} />)}
            </div>
          </Veld>
          <Veld label="Eigenaar">
            <Kies<string> waarde={eigenaar} onKies={setEigenaar}
              opties={[{ key: '', label: 'Niemand' }, ...kandidaten.map((m) => ({ key: m.id, label: m.naam }))]} />
          </Veld>
          <Veld label="Start"><input type="date" className="invoer" value={start} onChange={(e) => setStart(e.target.value)} /></Veld>
          <Veld label="Streefdatum"><input type="date" className="invoer" value={eind} onChange={(e) => setEind(e.target.value)} /></Veld>
          {githubAan && (
            <Veld label="Repository" hulp="Dan staan de commits, pull requests en issues bij het tabblad Code, en kun je een taak doorzetten als issue.">
              <Kies<string> waarde={repo} onKies={setRepo}
                opties={[{ key: '', label: 'Geen' }, ...repos.map((r) => ({ key: r.naam, label: r.naam }))]} />
            </Veld>
          )}
          <Veld label="Omschrijving" breed hulp="De AI leest dit mee als je hem iets over dit project vraagt.">
            <textarea className="invoer" rows={5} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
          </Veld>
        </div>
      </section>

      <section className="vlak">
        <div className="vlak-kop"><h2>Labels</h2></div>
        <div className="label-beheer">
          {labels.map((l, i) => (
            <div key={l.naam} className="label-regel">
              <span className={`label t-${l.kleur}`}>{l.naam}</span>
              <div className="kleurkeuze klein">
                {KLEUREN.map((k) => (
                  <button key={k} type="button" className={`kleur-bol t-${k} ${l.kleur === k ? 'actief' : ''}`}
                    onClick={() => setLabels(labels.map((x, j) => (j === i ? { ...x, kleur: k } : x)))} aria-label={k} />
                ))}
              </div>
              <Knop soort="stil" klein icoon={<X size={14} />} onClick={() => setLabels(labels.filter((_, j) => j !== i))} />
            </div>
          ))}
          <div className="rij">
            <input className="invoer klein" value={nieuwLabel} placeholder="Nieuw label, bijvoorbeeld frontend of review"
              onChange={(e) => setNieuwLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && nieuwLabel.trim() && !labels.some((l) => l.naam.toLowerCase() === nieuwLabel.trim().toLowerCase())) {
                  setLabels([...labels, { naam: nieuwLabel.trim().slice(0, 32), kleur: 'info' }])
                  setNieuwLabel('')
                }
              }} />
            <Knop klein icoon={<Plus size={14} />} disabled={!nieuwLabel.trim()}
              onClick={() => { setLabels([...labels, { naam: nieuwLabel.trim().slice(0, 32), kleur: 'info' }]); setNieuwLabel('') }} />
          </div>
        </div>
      </section>

      <section className="vlak">
        <div className="vlak-kop"><h2>Team</h2></div>
        <p className="stil">Wie in het team zit, ziet het project bovenaan in het menu. Taken toewijzen kan ook aan wie er niet in zit.</p>
        <div className="team-keuze">
          {kandidaten.map((m) => {
            const lid = team.find((t) => t.id === m.id)
            return (
              <div key={m.id} className={`team-lid ${lid ? 'actief' : ''}`}>
                <input type="checkbox" checked={!!lid}
                  onChange={() => setTeam(lid ? team.filter((t) => t.id !== m.id) : [...team, { id: m.id, rol: 'lid' }])} />
                <Avatar naam={m.naam} id={m.id} grootte={22} />
                <span>{m.naam}</span>
                {lid && (
                  <select className="invoer mini-select" value={lid.rol}
                    onChange={(e) => setTeam(team.map((t) => (t.id === m.id ? { ...t, rol: e.target.value } : t)))}>
                    <option value="lid">lid</option>
                    <option value="eigenaar">eigenaar</option>
                  </select>
                )}
              </div>
            )
          })}
        </div>
      </section>

      <div className="instellingen-voet">
        <Knop soort="gevaar" icoon={<Archive size={15} />} onClick={() => void archief()}>
          {project.archief ? 'Uit het archief halen' : 'In het archief zetten'}
        </Knop>
        <span className="vul" />
        <Knop soort="hoofd" icoon={<Save size={15} />} bezig={bezig} disabled={!naam.trim()} onClick={() => void bewaar()}>Opslaan</Knop>
      </div>
    </div>
  )
}
